import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useFormContext } from "react-hook-form";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { PaymentInstallments } from "./PaymentInstallments";
import { POINTS_RIGHTS_DUE_DATE } from "@/types";
import { useAuth } from "@/contexts/AuthContext";
import React from "react";
import { Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";


interface PaymentStepProps {
  numberOfInstallments: number;
  onInstallmentsChange: (value: number) => void;
  paymentInstallments: Array<{ date: string; amount: number; }>;
  onPaymentInstallmentChange: (index: number, field: 'date' | 'amount', value: string | number) => void;
  isEditMode?: boolean;
  paymentType: 'details' | 'installments';
  onPaymentTypeChange: (type: 'details' | 'installments') => void;
  isExplorer?: boolean;
}
export function PaymentStep({
  numberOfInstallments,
  onInstallmentsChange,
  paymentInstallments,
  onPaymentInstallmentChange,
  isEditMode = false,
  paymentType,
  onPaymentTypeChange,
  isExplorer = false,
}: PaymentStepProps) {
  const { register, setValue, watch, formState: { errors } } = useFormContext();
  const { user } = useAuth();
  const purchasePrice = watch('purchasePrice') || 0;

  // Check if user is a manager
  const isManager = user?.role === 'manager';


  // Get the current year and next year
  const currentYear = new Date().getFullYear();
  const nextYear = currentYear + 1;
  const yearAfterNext = currentYear + 2;

  const paymentTermsOptions = [
    { value: "Monthly", label: "Monthly" },
    { value: "One-Time", label: "One-Time" },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-5 gap-4">

        {!isExplorer && (
          <div className="space-y-2">
            <Label htmlFor="firstYearUse">First Year Use *</Label>
            <Select
              onValueChange={(value) => setValue("firstYearUse", Number(value))}
              value={String(watch('firstYearUse')) || String(nextYear)}
            >
              <SelectTrigger id="firstYearUse">
                <SelectValue placeholder="Select year" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={String(currentYear)}>{currentYear}</SelectItem>
                <SelectItem value={String(nextYear)}>{nextYear}</SelectItem>
                <SelectItem value={String(yearAfterNext)}>{yearAfterNext}</SelectItem>
              </SelectContent>
            </Select>
            <input
              {...register("firstYearUse", {
                required: !isExplorer ? "First Year Use is required" : false,
              })}
              type="hidden"
              value={watch("firstYearUse") || nextYear} // Default to next year (2026)
            />
          </div>
        )}
        <div className="space-y-2">
          <Label htmlFor="purchasePrice">Purchase Price *</Label>
          <Input
            id="purchasePrice"
            type="number"
            {...register("purchasePrice", {
              required: true,
              min: 0,
              valueAsNumber: true,
              setValueAs: (v) => (v === "" ? 0 : Number(v)),
            })}
            readOnly={isExplorer}
            className={isExplorer ? "bg-gray-100 text-gray-500 cursor-not-allowed" : ""}
          />
        </div>
        {(
          <React.Fragment>
            <div className="space-y-2">
              <Label htmlFor="paymentTerms">Payment Terms *</Label>
              <Select
                onValueChange={(value) => setValue("paymentTerms", value)}
                value={watch('paymentTerms') || "Monthly"}
              >
                <SelectTrigger id="paymentTerms">
                  <SelectValue placeholder="Select payment terms" />
                </SelectTrigger>
                <SelectContent>
                  {paymentTermsOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input
                {...register("paymentTerms", {
                  required: "Payment Terms is required",
                })}
                type="hidden"
                value={watch("paymentTerms") || "Monthly"}
              />
            </div>

            {!isExplorer && (
              <div className="space-y-2">
                <Label htmlFor="pointPrice">Point Price</Label>
                <div className="relative group">
                  <Input
                    id="pointPrice"
                    value={watch('pointPrice') ? watch('pointPrice') : ''} // eneuros
                    readOnly
                    className="bg-gray-100 text-gray-500 cursor-not-allowed"
                  />
                  <div className="absolute left-0 bottom-full mb-1 hidden w-max rounded bg-gray-700 p-2 text-white text-sm group-hover:block">
                    Service Charge Price Per Point
                  </div>
                </div>
              </div>
            )}
            {!isExplorer && (
              <div className="space-y-2">
                <Label htmlFor="pointsRightsDueDate">Points Rights Due Date</Label>
                <Input
                  id="pointsRightsDueDate"
                  type="number"
                  value={watch('pointsRightsDueDate') ? watch('pointsRightsDueDate') : POINTS_RIGHTS_DUE_DATE}
                  readOnly
                  className="bg-gray-100 text-gray-500 cursor-not-allowed"
                />
              </div>
            )}

          </React.Fragment>
        )} </div>
      {!isManager && (
        <Tabs
          value={paymentType}
          onValueChange={(value) => onPaymentTypeChange(value as 'details' | 'installments')}
          className="w-full"
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="installments">Payment Installments</TabsTrigger>
            <TabsTrigger value="details">Payment Details</TabsTrigger>
          </TabsList>

          <TabsContent value="installments" className="space-y-4">
            <PaymentInstallments
              numberOfInstallments={numberOfInstallments}
              onInstallmentsChange={onInstallmentsChange}
              paymentInstallments={paymentInstallments}
              onPaymentInstallmentChange={onPaymentInstallmentChange}
              purchasePrice={purchasePrice}
              isEditMode={isEditMode}
              maxInstallments={isExplorer ? 11 : undefined}
            />
          </TabsContent>

          <TabsContent value="details" className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label htmlFor="notes.paymentNotes">Payment Details</Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Custom payment information as text, installment feature should be your preference</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <Textarea
                id="notes.paymentNotes"
                {...register("notes.paymentNotes")}
                placeholder="15 Days from contract date"
                className="min-h-[150px]"
              />
            </div>
          </TabsContent>
        </Tabs>
      )}

      {isManager && (
        <div className="space-y-4">
          <PaymentInstallments
            numberOfInstallments={numberOfInstallments}
            onInstallmentsChange={onInstallmentsChange}
            paymentInstallments={paymentInstallments}
            onPaymentInstallmentChange={onPaymentInstallmentChange}
            purchasePrice={purchasePrice}
            isEditMode={isEditMode}
            maxInstallments={isExplorer ? 11 : undefined}
          />
        </div>
      )}
    </div>
  );
}